// Centralized API endpoint builders with env overrides

// Azure Web PubSub negotiate endpoint
export const webPubSubNegotiateUrl = () => {
  const configured = process.env.REACT_APP_WEBPUBSUB_NEGOTIATE_URL;
  if (process.env.NODE_ENV === "development") {
    return configured?.startsWith("/") ? configured : "/api/Negotiate";
  }
  return (
    configured ||
    "https://omnioalerts-ebedcybdcvbcahfz.southafricanorth-01.azurewebsites.net/api/Negotiate"
  );
};

// Logic App triggers for alert notifications
const buildAlertTriggerUrl = (envKey, fallback, facilityId) => {
  const base = (envKey && process.env[envKey]) || fallback;
  if (!base) return "";
  const encoded = encodeURIComponent(facilityId ?? "");
  if (base.includes("{facilityId}")) {
    return base.split("{facilityId}").join(encoded);
  }
  if (base.includes("{facility}")) {
    return base.replace("{facility}", encoded);
  }
  const facilityKey = "facility=";
  const existingIndex = base.indexOf(facilityKey);
  if (existingIndex !== -1) {
    const prefix = base.slice(0, existingIndex + facilityKey.length);
    return `${prefix}${encoded}`;
  }
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${facilityKey}${encoded}`;
};

const buildAlertTriggerUrlWithCycle = (envKey, fallback, facilityId, cycleId, parametres) => {
  const base = (envKey && process.env[envKey]) || fallback;
  if (!base) return "";
  const encodedFacility = encodeURIComponent(facilityId ?? "");
  const encodedCycle = encodeURIComponent(cycleId ?? "");
  const encodedParametres = encodeURIComponent(parametres ?? "");
  const hasFacilityToken =
    base.includes("{facilityId}") || base.includes("{facility_id}") || base.includes("{facility}");
  const hasCycleToken =
    base.includes("{cycleId}") || base.includes("{cycle_id}") || base.includes("{cycle}");
  const hasParametresToken = base.includes("{parametres}") || base.includes("{parametre}");
  let next = base;

  if (next.includes("{facilityId}")) next = next.split("{facilityId}").join(encodedFacility);
  if (next.includes("{facility_id}")) next = next.split("{facility_id}").join(encodedFacility);
  if (next.includes("{facility}")) next = next.split("{facility}").join(encodedFacility);
  if (next.includes("{cycleId}")) next = next.split("{cycleId}").join(encodedCycle);
  if (next.includes("{cycle_id}")) next = next.split("{cycle_id}").join(encodedCycle);
  if (next.includes("{cycle}")) next = next.split("{cycle}").join(encodedCycle);
  if (next.includes("{parametres}")) next = next.split("{parametres}").join(encodedParametres);
  if (next.includes("{parametre}")) next = next.split("{parametre}").join(encodedParametres);

  const facilityKey = "facility=";
  const cycleKey = "cycle=";
  const parametresKey = "parametres=";
  const parametreKey = "parametre=";
  const withFacility =
    next.includes(facilityKey) || hasFacilityToken
      ? next
      : `${next}${next.includes("?") ? "&" : "?"}${facilityKey}${encodedFacility}`;

  const withCycle =
    withFacility.includes(cycleKey) || hasCycleToken
      ? withFacility
      : `${withFacility}${withFacility.includes("?") ? "&" : "?"}${cycleKey}${encodedCycle}`;

  if (parametres == null) {
    return withCycle;
  }

  if (withCycle.includes(parametresKey) || withCycle.includes(parametreKey) || hasParametresToken) {
    return withCycle;
  }

  return `${withCycle}${withCycle.includes("?") ? "&" : "?"}${parametresKey}${encodedParametres}`;
};

const buildAlertTriggerUrlWithCycleAndDateRange = (
  envKey,
  fallback,
  facilityId,
  cycleId,
  startDate,
  endDate
) => {
  const base = (envKey && process.env[envKey]) || fallback;
  if (!base) return "";

  const replacements = [
    ["{facilityId}", encodeURIComponent(facilityId ?? "")],
    ["{facility_id}", encodeURIComponent(facilityId ?? "")],
    ["{facility}", encodeURIComponent(facilityId ?? "")],
    ["{cycleId}", encodeURIComponent(cycleId ?? "")],
    ["{cycle_id}", encodeURIComponent(cycleId ?? "")],
    ["{cycle}", encodeURIComponent(cycleId ?? "")],
    ["{startDate}", encodeURIComponent(startDate ?? "")],
    ["{start_date}", encodeURIComponent(startDate ?? "")],
    ["{endDate}", encodeURIComponent(endDate ?? "")],
    ["{end_date}", encodeURIComponent(endDate ?? "")],
  ];

  let next = base;
  replacements.forEach(([token, value]) => {
    if (next.includes(token)) next = next.split(token).join(value);
  });

  const ensureParam = (url, key, value) => {
    if (value == null) return url;
    const encodedValue = encodeURIComponent(value);
    const token = `${key}=`;
    const existingIndex = url.indexOf(token);
    if (existingIndex !== -1) {
      const suffixIndex = url.indexOf("&", existingIndex);
      const prefix = url.slice(0, existingIndex + token.length);
      const suffix = suffixIndex === -1 ? "" : url.slice(suffixIndex);
      return `${prefix}${encodedValue}${suffix}`;
    }
    return `${url}${url.includes("?") ? "&" : "?"}${token}${encodedValue}`;
  };

  next = ensureParam(next, "facility", facilityId);
  next = ensureParam(next, "cycle", cycleId);
  next = ensureParam(next, "start_date", startDate);
  next = ensureParam(next, "end_date", endDate);

  return next;
};

export const environmentalAlertsTriggerUrl = (facilityId) =>
  buildAlertTriggerUrl(
    "REACT_APP_ENVIRONMENTAL_ALERTS_URL",
    "https://prod-197.westeurope.logic.azure.com:443/workflows/6a5a89149016475998e17499fb0edfd3/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=dmYCrZ0HcfLcdEcEd8ioAmm1IAKEBS9X_lv11EKmHsY&facility=",
    facilityId
  );

export const kpiAlertsTriggerUrl = (facilityId) =>
  buildAlertTriggerUrl(
    "REACT_APP_KPI_ALERTS_URL",
    "https://prod-86.westeurope.logic.azure.com:443/workflows/42f10ea196324e44b822602fe06820e3/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=TKBXNQnuVG2B0l8xfPgps6Yn3dArRPdYhg4uPKMyx7k&facility=",
    facilityId
  );

export const activityAlertsTriggerUrl = (facilityId) =>
  buildAlertTriggerUrl(
    "REACT_APP_ACTIVITY_ALERTS_URL",
    "https://prod-29.westeurope.logic.azure.com:443/workflows/dee046384baa4e1a99b6d70fe655feca/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=kF0WuVVN3BQ40ENeG2TTy4y0g6YDVZFr8Msjepm0NgU&facility=",
    facilityId
  );

// Returns the full URL to fetch the broiler list for a user
export const broilerListUrl = (userID) => {
  const base =
    process.env.REACT_APP_BROILER_LIST_URL ||
    "https://prod-94.westeurope.logic.azure.com:443/workflows/1b1a8bdcfd8a43cebbbc9c22e1094205/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=QPVAnwjejMBDQ0XPJcxHIVlrzrWxwhAYIpQ1X6fRFp8&id=";
  return `${base}${encodeURIComponent(userID)}`;
};

const appendOrReplaceQueryParam = (url, key, value) => {
  const encoded = encodeURIComponent(value ?? "");
  const pattern = new RegExp(`([?&]${key}=)[^&]*`);
  if (pattern.test(url)) {
    return url.replace(pattern, `$1${encoded}`);
  }
  return `${url}${url.includes("?") ? "&" : "?"}${key}=${encoded}`;
};

// Returns the full URL to fetch broiler details for a facility and cycle
export const broilerDetailsUrl = (facilityId, cycleId) => {
  const base =
    process.env.REACT_APP_BROILER_DETAILS_URL ||
    "https://prod-153.westeurope.logic.azure.com:443/workflows/0baaa45a323b4a80b6e4bc3e61243a03/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=-pWB4I9XXHV-x0QXMVudyeA4GcBH987ru4aEsHH9Lh4&facility={facilityId}&cycle={cycleId}";
  const withFacility = base
    .split("{facilityId}")
    .join(encodeURIComponent(facilityId ?? ""))
    .split("{facility_id}")
    .join(encodeURIComponent(facilityId ?? ""));
  const withCycle = withFacility
    .split("{cycleId}")
    .join(encodeURIComponent(cycleId ?? ""))
    .split("{cycle_id}")
    .join(encodeURIComponent(cycleId ?? ""));
  return appendOrReplaceQueryParam(
    appendOrReplaceQueryParam(withCycle, "facility", facilityId),
    "cycle",
    cycleId
  );
};

// Dashboard endpoints (facility scoped)
export const mortalityUrl = (facilityId) =>
  buildAlertTriggerUrl(
    "REACT_APP_MORTALITY_URL",
    "https://prod-109.westeurope.logic.azure.com:443/workflows/70e908c886ee49df8025270ec8c91501/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=zYj1_2XkwcvXj4KCN0T4Ym20nzzdLGOgnLYy-INOGl0&facility={facilityId}",
    facilityId
  );

export const weightUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_WEIGHT_URL ||
    "https://prod-82.westeurope.logic.azure.com:443/workflows/87b34aa895d84636ac6328f84ae25d41/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=mvNiCGjFTJRKnYHnt7KsaQs758WizxpyDXHtLecJGAI&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const envUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_ENV_URL ||
    "https://prod-25.westeurope.logic.azure.com:443/workflows/94dd56ae3d294a84bf77bd3f53fc352c/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=FFN8lko4NiPZjBqKZn5I8rYhUApMirwQVvvdsgJk5Jg&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const luxUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_LUX_URL ||
    "https://prod-00.westeurope.logic.azure.com:443/workflows/8de6674984cf4a30bfa52d91095666a3/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=QN4bp5SROnU6qdowxhGlFpoSU1wircmGP8w-Tp8yfi8&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const floorTempUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_FLOOR_TEMP_URL ||
    "https://prod-219.westeurope.logic.azure.com:443/workflows/2ed2f2f2832a43119ae1b6a309d467ed/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=z_AhnFjv9M0GdqjWKucI-eDx_4RTjdgCIRYrZVV-twc&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const cardSummaryUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_CARD_SUMMARY_URL ||
    "https://prod-105.westeurope.logic.azure.com:443/workflows/6953254ec103475981e29af4f639a978/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=DKmndq_gwBE6heN_g8s6sJN1IA6_16Q14f3SrnwAKfI&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const feedUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_FEED_URL ||
    "https://prod-104.westeurope.logic.azure.com:443/workflows/a257240a91674dba82d39f3341314da8/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=o4s38P_DUa-dCHXhyBy8qDoK0gZGGXugFjNe13xbSwI&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const waterUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_WATER_URL",
    "https://prod-02.westeurope.logic.azure.com:443/workflows/221e59955d6b438087bac7813f15fe71/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=0aCgYZxRfjd5LguMfEaiZNr00dts-enw8lPbyL8llmk&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const waterQualityUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_WATER_QUALITY_URL ||
    "https://prod-250.westeurope.logic.azure.com:443/workflows/923101694abe47cab63bc2a639efa9db/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=IHxGoonqJyjrMTLb8P1JTgiT_W6JV7Pxy00oUnJ0GDk&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const dashboardSummaryMetricsUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_DASHBOARD_SUMMARY_METRICS_URL",
    "https://prod-250.westeurope.logic.azure.com:443/workflows/ee1a4f22687347108996c228bdaadaf1/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=_sNV3Ds89HX0tFxrmuX1SRO9NQVCnteOXcrCkjdr05c&facility={facility_id}&cycle={cycle_id}",
    facilityId,
    cycleId
  );

export const tempEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_TEMP_ENV_URL",
    "https://prod-238.westeurope.logic.azure.com:443/workflows/41e7fedfd93e4c0bb57ddf09f4388c4a/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=3OcVylGNJqIdygAsx58MOu_Mosj3lVXJnai_dnbU2G0&facility={facilityId}&cycle={cycleId}&parametre=Temp",
    facilityId,
    cycleId,
    "Temp"
  );

export const tempEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_TEMP_ENV_DAY_DETAIL_URL",
    "https://prod-33.westeurope.logic.azure.com:443/workflows/9d7749f7780e4585895173144622d21c/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=PvpAnjMDAKUMrmO1k9tdP6s478JwLO06ZdkSZa_HOmU&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const humEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_HUM_ENV_DAY_DETAIL_URL",
    "https://prod-206.westeurope.logic.azure.com:443/workflows/71243bfc31514d6a99b1abab58a46b0c/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=dUB_aHLNOdJWpI9y5z6sa-abK7wTA1CDnIhJyQCEXUQ&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const floorTempEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_FLOOR_TEMP_ENV_DAY_DETAIL_URL",
    "https://prod-51.westeurope.logic.azure.com:443/workflows/77faecd3946646938987036b2c48771e/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=4MUWrR8fEIgZwqu9laC-4HSW66w-_2T5ZbAp5WSMwd0&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const luxEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_LUX_ENV_DAY_DETAIL_URL",
    "https://prod-227.westeurope.logic.azure.com:443/workflows/c267e97bca0047a89ca54e56336272be/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=vmCV_CQlMgoFlkigKoCUA6hoLGl-YrKFzAUJQI2Bdoc&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const nh3EnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_NH3_ENV_DAY_DETAIL_URL",
    "https://prod-130.westeurope.logic.azure.com:443/workflows/17b8828265ba4c0b968a019bf47a0c0d/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=wFe6XMvyn-diYECxYfXZUKdrSCCdPHNyMbPL6Km33zQ&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const waterTempEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_WATER_TEMP_ENV_DAY_DETAIL_URL",
    "https://prod-192.westeurope.logic.azure.com:443/workflows/7d28a5f7db08434e8f39704e9a7a3a1c/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=acAbRFLReHPJHAT4I4e-KchYwZdXK-CeVqgtvtAQYq8&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const phEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_PH_ENV_DAY_DETAIL_URL",
    "https://prod-226.westeurope.logic.azure.com:443/workflows/6500920349094b42b61d6072c00a77a8/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=jepCxt3FDDPzIBJpm7M-lhP9-JSuBt3FEwyVDxccy9w&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const orpEnvDayDetailUrl = (facilityId, cycleId, startDate, endDate) =>
  buildAlertTriggerUrlWithCycleAndDateRange(
    "REACT_APP_ORP_ENV_DAY_DETAIL_URL",
    "https://prod-84.westeurope.logic.azure.com:443/workflows/23810e94e2b94588a9ea7c21c40dc317/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=alxRY_3VEypbSmP4yBUkt0CkHhuZCgSTU1dPJsre7so&facility={facility_id}&cycle={cycle_id}&start_date={date}&end_date={date}",
    facilityId,
    cycleId,
    startDate,
    endDate
  );

export const humEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_HUM_ENV_URL",
    "https://prod-201.westeurope.logic.azure.com:443/workflows/99648f19f0bb4e6aa9494b8893627e82/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=6cCoYuF1M1L12nTNFWIViB2flEN-As1y9VutU1wYlPY&facility={facilityId}&cycle={cycleId}&parametre=Hum",
    facilityId,
    cycleId,
    "Hum"
  );

export const nh3EnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_NH3_ENV_URL",
    "https://prod-226.westeurope.logic.azure.com:443/workflows/4ef126a2e5cf4d538d488890da927fc1/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=1kSeQKbNpvO2rj7W4LXeNZ1pcXunELgLHfPpOEbvSAQ&facility={facilityId}&cycle={cycleId}&parametre=NH3",
    facilityId,
    cycleId,
    "NH3"
  );

export const luxEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_LUX_ENV_URL",
    "https://prod-234.westeurope.logic.azure.com:443/workflows/aeaf5bf63e04441ca50424dbb4565df2/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=yfYQGP0aVJg5haVw5hKkCM5hJC6kvaxAPxr4dbtfSZQ&facility={facilityId}&cycle={cycleId}&parametre=Lux",
    facilityId,
    cycleId,
    "Lux"
  );

export const lightingDarkUrl = (facilityId, cycleId) => {
  const base =
    process.env.REACT_APP_LIGHTING_DARK_URL || "https://dvapi.omniofarm.com/getDarkPeriod";
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const floorTempEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_FTEMP_ENV_URL",
    "https://prod-95.westeurope.logic.azure.com:443/workflows/423c08be42f441599488403663df049e/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=NZfwF0jzwYxZO4PMLcvHANCrXE3JgoAn9YJLxrwNyT4&facility={facilityId}&cycle={cycleId}&parametre=FTemp",
    facilityId,
    cycleId,
    "FTemp"
  );

export const phEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_PH_ENV_URL",
    "https://prod-180.westeurope.logic.azure.com:443/workflows/ff4e8e572902409e929e7e74d2be23fb/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=O0KXcVacfErZE70UUustVnH8vHgnG2Eklev5yr3Vbm4&facility={facilityId}&cycle={cycleId}&parametre=pH",
    facilityId,
    cycleId,
    "pH"
  );

export const waterTempEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_WATER_TEMP_ENV_URL",
    "https://prod-65.westeurope.logic.azure.com:443/workflows/35fd64286f9d4a77b49966be19de52c9/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=z9spp0nluAlN2QBzsb2gPugzB2c-tSo6jhiF2sp5EXs&facility={facilityId}&cycle={cycleId}&parametre=WTemp",
    facilityId,
    cycleId
  );

export const orpEnvUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_ORP_ENV_URL",
    "https://prod-218.westeurope.logic.azure.com:443/workflows/59d77ce6d49341cdb9edeaaf564686eb/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=DdoE7oqEm0XHZnGKlHiKCag8MmJYLjBqhXkEBdtgop8&facility={facilityId}&cycle={cycleId}&parametre=ORP",
    facilityId,
    cycleId,
    "ORP"
  );

export const performanceKpiUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_PERFORMANCE_KPI_URL",
    "https://prod-120.westeurope.logic.azure.com:443/workflows/5160d259a9f24065842438fbc67e4a09/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=TulazNO0yjXeDDkkml-d6ggdb32bI5j4CXqZjwT_WCw&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const performanceWeightUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_PERFORMANCE_WEIGHT_URL",
    "https://prod-249.westeurope.logic.azure.com:443/workflows/0ec9241f67624048ad3f6c287ab3f0cb/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=QUxqR-rZun00N7qMWZOqEcYvCQFlIdR1P0Alppu-rw4&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const performanceFcrUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_PERFORMANCE_FCR_URL",
    "https://prod-154.westeurope.logic.azure.com:443/workflows/0313540964c443b399495c91fd3075c6/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=3r_Ob9PvNilh1-FjuF_PnH2uw7o00TiA5fVHyhOWwc0&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const performanceActivitiesUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_PERFORMANCE_ACTIVITIES_URL",
    "https://prod-127.westeurope.logic.azure.com:443/workflows/0e38a25ed69f46259785e16eee6e00f0/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=yA9rLz1XlT_FjHQxW4QLWExcxikQefu1Mk-JintO8J4&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const reportsFeedUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_REPORTS_FEED_URL",
    "https://prod-167.westeurope.logic.azure.com:443/workflows/50863b49f3b740a5bf14c3901df593b0/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=suua8uEz6rmPsppH1h7w9vM_EDnn-NGbB0RtLPzdP88&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const reportsWaterUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_REPORTS_WATER_URL",
    "https://prod-08.westeurope.logic.azure.com:443/workflows/dfe38dee8f2a44dc98fd523b69c36e3a/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=RzhC0ZJJ9Boq2_pqr4IhbJS4MlkczCa4FRodl7M5PoY&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const reportsSummaryUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_REPORTS_SUMMARY_URL",
    coreApiUrl("/getReportEnvSummary/{facility_id}/{cycle_id}"),
    facilityId,
    cycleId
  );

export const reportsPerformanceUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_REPORTS_PERFORMANCE_URL",
    coreApiUrl("/getReportKpiSummary/{facilityId}/{cycleId}"),
    facilityId,
    cycleId
  );

export const reportsTopIssuesEnvironmentUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_REPORTS_TOP_ISSUES_ENVIRONMENT_URL",
    "https://prod-62.westeurope.logic.azure.com:443/workflows/dd0bedaf6d574b8281a50cd8087a74b3/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=IdpfzwTDkaMqUDHtR-NXL05qlL1Kee5_V8j6U08VFp8&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

export const reportsTopIssuesPerformanceUrl = (facilityId, cycleId) =>
  buildAlertTriggerUrlWithCycle(
    "REACT_APP_REPORTS_TOP_ISSUES_PERFORMANCE_URL",
    "https://prod-218.westeurope.logic.azure.com:443/workflows/be7afc888a264febb252563c7a0cbdf5/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=yv1R2zT165ldVZyboCfDKkFSzZ-8cPI84ag36TgIF-M&facility={facilityId}&cycle={cycleId}",
    facilityId,
    cycleId
  );

// Averages endpoints (facility scoped)
export const avgTempUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_TEMP_URL ||
    "https://prod-92.westeurope.logic.azure.com:443/workflows/eca63a613bf04b8c9d19d872d63bddbd/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=HZ_zuXejJtiGVPWCW-0PbzbEXG2UX0QiZOdtuPuiiS4&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgHumUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_HUM_URL ||
    "https://prod-166.westeurope.logic.azure.com:443/workflows/dc1c2dafed4f481d9eec9b84dc8217b7/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=glIjaYM9iuUtNxFABfZJGJE9oSDRo4zj_aLFahbktFI&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgFloorTempUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_FLOOR_TEMP_URL ||
    "https://prod-103.westeurope.logic.azure.com:443/workflows/a5cc12753c61421cb1232707c7ed459b/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=NjaY8MC5uNeF_Ba7EuN7Qmw96gpNMwurgIxzuPCbMMA&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgNH3Url = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_NH3_URL ||
    "https://prod-37.westeurope.logic.azure.com:443/workflows/ce7f1488578a4deebebad4a82a1d2def/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=RRGdJHArrIrdvtoukTeWveoAER-YBvUonNMB-7oRYeI&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgCyclePerformanceKpiUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_CYCLE_PERFORMANCE_KPI_URL ||
    "https://prod-183.westeurope.logic.azure.com:443/workflows/8b99cfca6c2343768b4aa7193148fe84/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=nxFBgla6wl-dpwRrmWsCr2BOyRgzGNba05eZQQFkb2o&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgLuxUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_LUX_URL ||
    "https://prod-77.westeurope.logic.azure.com:443/workflows/5bf30ae5ddf74b289e28140d9b210494/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=1uQdIDwZjg7hWDWKhmvaNsFhlpVqKbZcpBg6gKATgb0&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgDarkUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_DARK_URL ||
    "https://prod-231.westeurope.logic.azure.com:443/workflows/8a0c0b8af356446eac98e7abb084fae3/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=9cLyRfor7c3uolGkkPkn5g1Fv8RO9KegkeK3f8EmZjk&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgWaterTempUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_WATER_TEMP_URL ||
    "https://prod-36.westeurope.logic.azure.com:443/workflows/3dc9e9daeb894b78975ee981555c9e64/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=LldKt-978QuEPaRQvecTSjDdBExAGRriF92PcYDlBJU&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgWaterPhUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_WATER_PH_URL ||
    "https://prod-47.westeurope.logic.azure.com:443/workflows/fba1be34cd584dda94cb3e8840cb37a3/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=D23Afz99gZdeMqBV6zdpw7dczTqQkVkvw9g7ps_sPgo&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

export const avgActivityUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_AVG_ACTIVITY_URL ||
    "https://prod-75.westeurope.logic.azure.com:443/workflows/42cc144050b148d59160d235cb22418b/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=iooXjt7miBCxrbZi9QCGHcJ4IJmT_kW3R2qiJdZW_ts&facility=";
  return `${base}${encodeURIComponent(facilityId)}`;
};

// Authentication and core API (non-LogicApp)
const getCoreApiBaseUrl = () => {
  const configured = process.env.REACT_APP_CORE_API_BASE_URL || "";
  if (configured) return configured.replace(/\/$/, "");
  return "https://dvapi.omniofarm.com";
};

const coreApiUrl = (path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const base = getCoreApiBaseUrl();
  return base ? `${base}${normalizedPath}` : normalizedPath;
};

const buildCoreApiUrl = (envKey, path, fallback) => {
  if (process.env.NODE_ENV === "development") return path;
  const configured = process.env[envKey];
  return configured || fallback;
};

export const authTokenUrl = () =>
  buildCoreApiUrl("REACT_APP_AUTH_TOKEN_URL", "/token", coreApiUrl("/token"));

export const authUserUrl = () =>
  buildCoreApiUrl("REACT_APP_AUTH_USER_URL", "/getUser", coreApiUrl("/getUser"));

export const userAddUrl = (userId) => {
  const base = buildCoreApiUrl("REACT_APP_USER_ADD_URL", "/getUserAdd", coreApiUrl("/getUserAdd"));
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(userId ?? "")}`;
};

export const updateUserAdditionalUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_UPDATE_USER_ADDITIONAL_URL",
    "/updateUserAdditional",
    coreApiUrl("/updateUserAdditional")
  );

export const verifyUserEmailUrl = (uniqueID) => {
  const base = buildCoreApiUrl(
    "REACT_APP_VERIFY_USER_EMAIL_URL",
    "/verifyUserEmail",
    coreApiUrl("/verifyUserEmail")
  );
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(uniqueID ?? "")}`;
};

export const farmsUrl = () =>
  buildCoreApiUrl("REACT_APP_FARMS_URL", "/getFarmsForUser", coreApiUrl("/getFarmsForUser"));

// Compose farms-for-user URL by appending the user ID
export const farmsForUserUrl = (userId, sectorType) => {
  const base = farmsUrl();
  const trimmed = base.endsWith("/") ? base : `${base}/`;
  const userUrl = `${trimmed}${encodeURIComponent(userId)}`;
  return sectorType == null ? userUrl : `${userUrl}/${encodeURIComponent(sectorType)}`;
};

export const hatcheryDispatchUrl = (farmId) => {
  const base = buildCoreApiUrl(
    "REACT_APP_HATCHERY_DISPATCH_URL",
    "/getHatcheryDispatch",
    coreApiUrl("/getHatcheryDispatch")
  );
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return farmId == null ? trimmed : `${trimmed}/${encodeURIComponent(farmId)}`;
};

export const allFarmsUrl = () =>
  buildCoreApiUrl("REACT_APP_ALL_FARMS_URL", "/getAllFarms", coreApiUrl("/getAllFarms"));

export const allFarmsByIdUrl = (farmId) => {
  const base = allFarmsUrl();
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}`;
};

export const dispatchStatusUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_DISPATCH_STATUS_URL",
    "/getDispatchStatus",
    coreApiUrl("/getDispatchStatus")
  );

export const updateHatcheryDispatchUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_UPDATE_HATCHERY_DISPATCH_URL",
    "/updateHatcheryDispatch",
    coreApiUrl("/updateHatcheryDispatch")
  );

// Get all broilers/facilities (for names and metadata)
export const allBroilersUrl = () =>
  buildCoreApiUrl("REACT_APP_ALL_BROILERS_URL", "/getAllBroilers", coreApiUrl("/getAllBroilers"));

// Get broiler data dictionary (e.g., management plans)
export const broilerDataUrl = () =>
  buildCoreApiUrl("REACT_APP_BROILER_DATA_URL", "/getBroilerData", coreApiUrl("/getBroilerData"));

export const broilerDataByFarmUrl = (farmId) => {
  const base = broilerDataUrl();
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return farmId != null ? `${trimmed}/${encodeURIComponent(farmId)}` : trimmed;
};

export const broilerDataByFarmAndTypeUrl = (farmId, type) => {
  const base = broilerDataByFarmUrl(farmId);
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return type != null ? `${trimmed}/${encodeURIComponent(type)}` : trimmed;
};

export const broilersByFarmUrl = (farmId) => {
  const base = process.env.REACT_APP_BROILERS_BY_FARM_URL || coreApiUrl("/getBroilers");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}`;
};

export const cyclesByFacilityUrl = (facilityId) => {
  const base = process.env.REACT_APP_CYCLES_BY_FACILITY_URL || coreApiUrl("/getCycleId");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}`;
};

export const broilersByFarmAndFacilityUrl = (farmId, facilityId) => {
  const base = process.env.REACT_APP_BROILERS_BY_FARM_FACILITY_URL || coreApiUrl("/getBroilers");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}/${encodeURIComponent(facilityId)}`;
};

export const farmStrainUrl = () =>
  buildCoreApiUrl("REACT_APP_FARM_STRAIN_URL", "/getFarmStrain", coreApiUrl("/getFarmStrain"));

export const farmStrainByFarmUrl = (farmId) => {
  const base = farmStrainUrl();
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return farmId != null ? `${trimmed}/${encodeURIComponent(farmId)}` : trimmed;
};

export const createFarmStrainUrl = () =>
  process.env.REACT_APP_CREATE_FARM_STRAIN_URL || coreApiUrl("/createFarmStrain");

export const updateFarmStrainUrl = () =>
  process.env.REACT_APP_UPDATE_FARM_STRAIN_URL || coreApiUrl("/updateFarmStrain");

export const removeFarmStrainUrl = () =>
  process.env.REACT_APP_REMOVE_FARM_STRAIN_URL || coreApiUrl("/removeFarmStrain");

export const convertStrainUrl = () =>
  buildCoreApiUrl("REACT_APP_CONVERT_STRAIN_URL", "/convertStrain", coreApiUrl("/convertStrain"));

export const actTypesUrl = () =>
  buildCoreApiUrl("REACT_APP_ACT_TYPES_URL", "/getActTypes", coreApiUrl("/getActTypes"));

const localInsightsUrl = (path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (process.env.NODE_ENV === "development") {
    return `/localinsights${normalizedPath}`;
  }
  const base = (
    process.env.REACT_APP_LOCAL_INSIGHTS_BASE_URL || "https://localinsights.omniofarm.com"
  ).replace(/\/$/, "");
  return `${base}${normalizedPath}`;
};

export const strainDetailsUrl = (strainId) =>
  `${localInsightsUrl("/v1/api/strain")}/${encodeURIComponent(strainId)}`;

export const practiceListUrl = () => localInsightsUrl("/v1/api/practice/list");

export const practiceCreateUrl = () => localInsightsUrl("/v1/api/practice/create");

export const updateBroilerDataUrl = () =>
  process.env.REACT_APP_UPDATE_BROILER_DATA_URL || coreApiUrl("/updateBroilerData");

export const removeBroilerDataUrl = () =>
  process.env.REACT_APP_REMOVE_BROILER_DATA_URL || coreApiUrl("/removeBroilerData");

// Get hatchery data for a facility, optionally scoped to a cycle
export const hatcheryDataUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_HATCHERY_DATA_URL || coreApiUrl("/getHatcheryData");
  const trimmed = base.endsWith("/") ? base : `${base}/`;
  const facilityUrl = `${trimmed}${encodeURIComponent(facilityId)}`;
  return cycleId == null ? facilityUrl : `${facilityUrl}/${encodeURIComponent(cycleId)}`;
};

// Get DOC records for a facility and cycle
export const docDataUrl = (facilityId, cycleId, invoiceNumber) => {
  const base = process.env.REACT_APP_DOC_DATA_URL || coreApiUrl("/getDOCData");
  const trimmed = base.endsWith("/") ? base : `${base}/`;
  const cycleUrl = `${trimmed}${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
  return invoiceNumber == null || String(invoiceNumber).trim() === ""
    ? cycleUrl
    : `${cycleUrl}/${encodeURIComponent(invoiceNumber)}`;
};

// Get mortalities for a facility and cycle
export const mortalitiesUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_MORTALITIES_URL || coreApiUrl("/getMortalities");
  const trimmed = base.endsWith("/") ? base : `${base}/`;
  return `${trimmed}${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

// Get mortality event definitions (id -> eventName)
export const mortEventsUrl = () =>
  process.env.REACT_APP_MORT_EVENTS_URL || coreApiUrl("/getMortEvents");

// Get cull event definitions (id -> eventName)
export const cullEventsUrl = () =>
  process.env.REACT_APP_CULL_EVENTS_URL || coreApiUrl("/getCullEvents");

// Get broiler settings for a facility (used for cycle dates)
export const broilerSettingsUrl = (facilityId) => {
  const base = process.env.REACT_APP_BROILER_SETTINGS_URL || coreApiUrl("/getBroilerSettings");
  const trimmed = base.endsWith("/") ? base : `${base}/`;
  return `${trimmed}${encodeURIComponent(facilityId)}`;
};

// Update broiler settings (placement and catch dates)
export const updateBroilerSettingsUrl = () =>
  process.env.REACT_APP_UPDATE_BROILER_SETTINGS_URL || coreApiUrl("/updateBroilerSettings");

// Create/update production cycle.
export const updateCyclesUrl = () =>
  process.env.REACT_APP_UPDATE_CYCLES_URL || coreApiUrl("/updateCycles");

export const removeCyclesUrl = () =>
  process.env.REACT_APP_REMOVE_CYCLES_URL || coreApiUrl("/removeCycles");

export const getCyclesUrl = () => process.env.REACT_APP_GET_CYCLES_URL || coreApiUrl("/getCycles");

export const getBroilerSummaryUrl = (farmId, cycleName, cycleDay) => {
  const base =
    process.env.REACT_APP_GET_BROILER_SUMMARY_URL ||
    "https://prod-183.westeurope.logic.azure.com:443/workflows/d9ec3f11031e49e1a0dd53c42c9dfdbf/triggers/When_an_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_an_HTTP_request_is_received%2Frun&sv=1.0&sig=TkisDnwec2Bk57cFsVA85g5_FlPn34_mxRSrh91G1zM";
  const url = new URL(base);
  url.searchParams.set("farm", farmId ?? "");
  url.searchParams.set("cycleName", cycleName ?? "");
  url.searchParams.set("day", cycleDay ?? "");
  return url.toString();
};

export const getCatchSummaryUrl = (farmId, cycleId) => {
  const base = process.env.REACT_APP_GET_CATCH_SUMMARY_URL || coreApiUrl("/getCatchSummary");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}/${encodeURIComponent(cycleId)}`;
};

export const getCostSummaryUrl = (farmId, cycleId) => {
  const base = process.env.REACT_APP_GET_COST_SUMMARY_URL || coreApiUrl("/getCostSummary");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}/${encodeURIComponent(cycleId)}`;
};

// Update or insert hatchery data (POST)
export const updateHatcheryDataUrl = () =>
  process.env.REACT_APP_UPDATE_HATCHERY_DATA_URL || coreApiUrl("/updateHatcheryData");

export const removeHatcheryDataUrl = () =>
  process.env.REACT_APP_REMOVE_HATCHERY_DATA_URL || coreApiUrl("/removeHatcheryData");

// Insert a new DOC details row (POST)
export const updateDOCTableUrl = () =>
  process.env.REACT_APP_UPDATE_DOC_TABLE_URL || coreApiUrl("/updateDOCTable");

// Remove a DOC details row (POST)
export const removeDOCTableUrl = () =>
  process.env.REACT_APP_REMOVE_DOC_TABLE_URL || coreApiUrl("/removeDOCTable");

// Create/Update Production Activities (POST)
// Note: API path intentionally matches server spelling 'updateActvities'
export const updateActivitiesUrl = () =>
  process.env.REACT_APP_UPDATE_ACTIVITIES_URL || coreApiUrl("/updateActvities");

// Get Production Activities for a facility + cycle (GET)
export const activitiesDataUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_ACTIVITIES_DATA_URL || coreApiUrl("/getActData");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

// Remove an activity by id (POST)
export const removeActivitiesUrl = () =>
  process.env.REACT_APP_REMOVE_ACTIVITIES_URL || coreApiUrl("/removeActivities");

// Get device list for notifications (GET)
export const deviceDetailsUrl = (facilityId) => {
  const base = process.env.REACT_APP_DEVICE_DETAILS_URL || coreApiUrl("/getDeviceDetails");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}`;
};

// Get detailed device limits for a specific device (GET)
export const actNotificationsUrl = (facilityId) => {
  const base = process.env.REACT_APP_ACT_NOTIFICATIONS_URL || coreApiUrl("/getActNotifications");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}`;
};

export const updateActNotificationsUrl = () =>
  process.env.REACT_APP_UPDATE_ACT_NOTIFICATIONS_URL || coreApiUrl("/updateActNotifications");

export const deviceDetailsForDeviceUrl = (facilityId, deviceId) => {
  const base =
    process.env.REACT_APP_DEVICE_DETAILS_FOR_DEVICE_URL ||
    process.env.REACT_APP_DEVICE_DETAILS_URL ||
    coreApiUrl("/getDeviceDetails");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(deviceId)}`;
};

export const kpiNotificationsUrl = (facilityId) => {
  const base = process.env.REACT_APP_KPI_NOTIFICATIONS_URL || coreApiUrl("/getKpiNotifications");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}`;
};

// Update device configuration (POST)
export const updateDeviceUrl = (deviceDataId) => {
  const base = process.env.REACT_APP_UPDATE_DEVICE_URL || coreApiUrl("/updateDevice");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(deviceDataId)}`;
};

// Get placement weights for a facility and cycle
export const placementWeightsUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_PLACEMENT_WEIGHTS_URL || coreApiUrl("/getPlacementWeights");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};
// Create or update mortalities/culls row (POST)
export const updateMortalitiesUrl = () =>
  process.env.REACT_APP_UPDATE_MORTALITIES_URL || coreApiUrl("/updateMortalities");

// Get Mortality description options (id -> eventName)
export const mortDescriptionsUrl = () =>
  process.env.REACT_APP_MORTALITY_DATA_URL || coreApiUrl("/getMortalityData");

// Get Cull description options (id -> eventName)
export const cullDescriptionsUrl = () =>
  process.env.REACT_APP_CULL_DATA_URL || coreApiUrl("/getCullData");

// Remove a mortalities/culls row (POST)
export const removeMortalitiesUrl = () =>
  process.env.REACT_APP_REMOVE_MORTALITIES_URL || coreApiUrl("/removeMortalities");

// Create or update placement weight (POST)
export const updateWeightUrl = () =>
  process.env.REACT_APP_UPDATE_WEIGHT_URL || coreApiUrl("/updateWeight");

// Remove a placement weight row (POST)
export const removePlacementWeightUrl = () =>
  process.env.REACT_APP_REMOVE_PLACEMENT_WEIGHT_URL || coreApiUrl("/removePlacementWeight");

// Get during-cycle broiler weights for a facility and cycle
export const broilerWeightsUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_BROILER_WEIGHTS_URL || coreApiUrl("/getBroilerWeights");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

// Get catch weights for a facility
export const catchDataUrl = (facilityId) => {
  const base = process.env.REACT_APP_CATCH_DATA_URL || coreApiUrl("/getCatchData");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}`;
};

// Remove Feed schedule (POST)
export const removeFeedUrl = () =>
  process.env.REACT_APP_REMOVE_FEED_URL || coreApiUrl("/removeFeed");

// Create or update Feed schedule (POST)
export const updateFeedUrl = () =>
  process.env.REACT_APP_UPDATE_FEED_URL || coreApiUrl("/updateFeed");

export const updateFeedTotalUrl = () =>
  process.env.REACT_APP_UPDATE_FEED_TOTAL_URL || coreApiUrl("/updateFeedTotal");

export const updateUtilitiesUrl = () =>
  process.env.REACT_APP_UPDATE_UTILITIES_URL || "https://dvapi.omniofarm.com/updateUtilities";

export const utilitiesUrl = (farmId, facilityId, cycleId) => {
  const base = process.env.REACT_APP_UTILITIES_URL || "https://dvapi.omniofarm.com/getUtilities";
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}/${encodeURIComponent(
    facilityId
  )}/${encodeURIComponent(cycleId)}`;
};

// Get Feed/Water consumption for a facility and cycle
export const feedScheduleUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_FEED_SCHEDULE_URL || "https://dvapi.omniofarm.com/getFeed";
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const getFeedBinsUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_FEED_BINS_URL || coreApiUrl("/getFeedBins");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const getFeedBalanceUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_FEED_BALANCE_URL || coreApiUrl("/getFeedBalance");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const feedCostsUrl = (farmId) => {
  const base = buildCoreApiUrl(
    "REACT_APP_FEED_COSTS_URL",
    "/getFeedCosts",
    coreApiUrl("/getFeedCosts")
  );
  if (farmId == null || farmId === "") return base;
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(farmId)}`;
};

export const feedCostsByFacilityCycleUrl = (facilityId, cycleId) => {
  const base = buildCoreApiUrl(
    "REACT_APP_FEED_COSTS_URL",
    "/getFeedCosts",
    coreApiUrl("/getFeedCosts")
  );
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const updateFeedSpendUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_UPDATE_FEED_SPEND_URL",
    "/updateFeedSpend",
    coreApiUrl("/updateFeedSpend")
  );

export const feedSpendByFacilityCycleUrl = (facilityId, cycleId) => {
  const base = buildCoreApiUrl(
    "REACT_APP_FEED_SPEND_URL",
    "/getFeedSpend",
    coreApiUrl("/getFeedSpend")
  );
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const updateFeedCostsUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_UPDATE_FEED_COSTS_URL",
    "/updateFeedCosts",
    coreApiUrl("/updateFeedCosts")
  );

export const removeFeedCostsUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_REMOVE_FEED_COSTS_URL",
    "/removeFeedCosts",
    coreApiUrl("/removeFeedCosts")
  );

export const waterFeedDataUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_WATER_FEED_DATA_URL || coreApiUrl("/getWaterFeedData");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

export const waterDataUrl = (facilityId, cycleId) => {
  const base = process.env.REACT_APP_WATER_DATA_URL || coreApiUrl("/getWaterData");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${encodeURIComponent(facilityId)}/${encodeURIComponent(cycleId)}`;
};

// Create or update Feed/Water consumption (POST)
export const updateFeedWaterUrl = () =>
  process.env.REACT_APP_UPDATE_FEED_WATER_URL || coreApiUrl("/AddUpdateFeedWater");

export const updateWaterDataUrl = () =>
  process.env.REACT_APP_UPDATE_WATER_DATA_URL || coreApiUrl("/updateWaterData");

// Remove a Feed/Water consumption row (POST)
export const removeFeedWaterUrl = () =>
  process.env.REACT_APP_REMOVE_FEED_WATER_URL || coreApiUrl("/removeFeedWater");

export const removeFeedSpendUrl = () =>
  buildCoreApiUrl(
    "REACT_APP_REMOVE_FEED_SPEND_URL",
    "/removeFeedSpend",
    coreApiUrl("/removeFeedSpend")
  );

export const removeWaterDataUrl = () =>
  process.env.REACT_APP_REMOVE_WATER_DATA_URL || coreApiUrl("/removeWaterData");

// Create or update Catch records (POST)
export const updateCatchDataUrl = () =>
  process.env.REACT_APP_UPDATE_CATCH_DATA_URL || coreApiUrl("/updateCatchData");

// Create or update during-cycle broiler weights (POST)
export const updateBroilerWeightsUrl = () =>
  process.env.REACT_APP_ADD_UPDATE_BROILER_WEIGHTS_URL || coreApiUrl("/updateWeights");

// Remove a during-cycle broiler weight row (POST)
export const removeBroilerWeightUrl = () =>
  process.env.REACT_APP_REMOVE_BROILER_WEIGHT_URL || coreApiUrl("/removeBroilerWeight");

// Remove a catch row (POST)
export const removeCatchUrl = () =>
  process.env.REACT_APP_REMOVE_CATCH_URL || coreApiUrl("/removeCatch");
export const updateKpiNotificationsUrl = (facilityId) => {
  const base =
    process.env.REACT_APP_UPDATE_KPI_NOTIFICATIONS_URL || coreApiUrl("/updateKpiNotifications");
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  if (!facilityId && facilityId !== 0) {
    return trimmed;
  }
  return `${trimmed}/${encodeURIComponent(facilityId)}`;
};
